/**
 * Utilities for extracting form structure for dependency map building
 */

import type { DataProvider, FieldElement } from '../../types';
import type { DependencySource, FormStructureForDependencyMap } from './types';

/**
 * Compare saved and processed dependencies to identify their source
 */
function compareDependencies(
  saved: Record<string, string[]>,
  processed: Record<string, string[]>
): DependencySource {
  const savedKeys = new Set(Object.keys(saved));
  const processedKeys = new Set(Object.keys(processed));

  const onlySaved = Array.from(savedKeys).filter((k) => !processedKeys.has(k));
  const onlyProcessed = Array.from(processedKeys).filter(
    (k) => !savedKeys.has(k)
  );

  if (onlySaved.length > 0 && onlyProcessed.length > 0) {
    return {
      source: 'both',
      savedDependencies: saved,
      extractedDependencies: processed,
    };
  }

  if (onlyProcessed.length > 0) {
    return {
      source: 'extracted',
      extractedDependencies: processed,
    };
  }

  if (onlySaved.length > 0) {
    return {
      source: 'saved',
      savedDependencies: saved,
    };
  }

  // Both are empty or identical
  return {
    source: Object.keys(saved).length > 0 ? 'saved' : 'extracted',
    savedDependencies: saved,
    extractedDependencies: processed,
  };
}

/**
 * Flatten nested field elements (sections/rows) into a flat array of fields
 */
function flattenFields(elements: FieldElement[]): Array<{
  id: string;
  fieldName?: string;
  dataProviderId?: string;
  expressions?: Array<{
    type: string;
    value: Array<{ kind: string; value: string; selected: boolean }>;
    enabled: boolean;
  }>;
  defaultValue?: { value: string; isExpression: boolean };
}> {
  const fields: Array<{
    id: string;
    fieldName?: string;
    dataProviderId?: string;
    expressions?: Array<{
      type: string;
      value: Array<{ kind: string; value: string; selected: boolean }>;
      enabled: boolean;
    }>;
    defaultValue?: { value: string; isExpression: boolean };
  }> = [];

  const traverse = (items: FieldElement[]): void => {
    items.forEach((item) => {
      // Check if this is a field (not a section or row)
      if (item.type === 'field' && 'fieldName' in item) {
        fields.push({
          id: item.id,
          fieldName: item.fieldName,
          dataProviderId:
            'dataProviderId' in item ? item.dataProviderId : undefined,
          expressions:
            'expressions' in item
              ? (item.expressions as (typeof fields)[number]['expressions'])
              : undefined,
          defaultValue:
            'defaultValue' in item
              ? (item.defaultValue as (typeof fields)[number]['defaultValue'])
              : undefined,
        });
      }

      // Recurse into children if they exist
      if ('children' in item && item.children) {
        traverse(item.children);
      }
    });
  };

  traverse(elements);

  return fields;
}

/**
 * Extract form structure from form data for dependency map building
 *
 * @param canvasFields - Array of field elements from the form
 * @param dataProviders - Original/saved data providers
 * @param processedDataProviders - Optional processed data providers with extracted dependencies (preview mode)
 * @returns Form structure for dependency map building
 */
// eslint-disable-next-line import/prefer-default-export
export function extractFormStructureForDependencyMap(
  canvasFields: FieldElement[],
  dataProviders: Record<string, DataProvider>,
  processedDataProviders?: Record<string, DataProvider>
): FormStructureForDependencyMap {
  // Flatten nested fields
  const flatFields = flattenFields(canvasFields);

  // Determine if we should compare saved vs processed
  // Only compare if processedDataProviders is provided AND different from dataProviders
  const shouldCompare =
    processedDataProviders !== undefined &&
    processedDataProviders !== dataProviders;

  // Extract dependencies from data providers
  const extractedDataProviders: Record<
    string,
    {
      dependencies?: Record<string, string[]>;
      dependencySource?: DependencySource;
    }
  > = {};

  // Always iterate over saved dataProviders (source of truth)
  Object.entries(dataProviders).forEach(([providerId, savedProvider]) => {
    const savedDeps = savedProvider.dependencies || {};
    const effectiveProcessedProvider =
      shouldCompare && processedDataProviders?.[providerId];
    let processedDeps: Record<string, string[]> = savedDeps; // Default to saved

    // Get processed dependencies if available and different
    if (effectiveProcessedProvider && effectiveProcessedProvider.dependencies) {
      processedDeps = effectiveProcessedProvider.dependencies;
    }

    // Compare if we have both saved and processed (and they're different)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const dependencySource =
      shouldCompare && processedDeps !== savedDeps
        ? compareDependencies(savedDeps, processedDeps)
        : { source: 'saved' as const, savedDependencies: savedDeps };

    extractedDataProviders[providerId] = {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      dependencies: shouldCompare ? processedDeps : savedDeps, // Use processed if comparing
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      dependencySource,
    };
  });

  return {
    canvasFields: flatFields,
    dataProviders: extractedDataProviders,
  };
}
